/**
 * Reports Page
 * Financial reports and analytics with data preview
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { apiGet, apiDownload, extractApiData } from '../utils/apiClient';
import {
  exportContractRegister,
  exportPaymentSchedule,
  exportInvestorSummary,
  exportAuditTrail,
  exportCurrentLongTerm,
  exportRolledContracts,
  exportRolloverFundFlow,
  exportContractSuspense,
} from '../utils/excelExport';
import { format } from 'date-fns';
import { formatCurrency as formatCurrencyBase, formatNumber as formatNum } from '../utils/formatters';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
};

// Status chip colors
const getStatusColor = (status) => {
  const colors = {
    'Active': 'success',
    'Approved': 'info',
    'Pending Approval': 'warning',
    'Draft': 'default',
    'Matured': 'secondary',
    'Cancelled': 'error',
  };
  return colors[status] || 'default';
};

function ReportsPage() {
  const { isFeatureEnabled, tenant } = useTenant();
  const { isAdmin } = usePermissions();
  const dp = tenant?.roundingPrecision ?? 3;

  // Format currency values
  const formatCurrency = (amount, currency = 'ZAR') => formatCurrencyBase(amount, currency, dp);
  const [tabValue, setTabValue] = useState(0);

  // Contract Register state
  const [contractFilters, setContractFilters] = useState({
    status: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [contractData, setContractData] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState(null);

  // Payment Schedule state
  const [paymentFilters, setPaymentFilters] = useState({
    dateFrom: '',
    dateTo: '',
  });
  const [paymentData, setPaymentData] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Investor Summary state
  const [investorFilters, setInvestorFilters] = useState({
    currency: 'all',
  });
  const [investorData, setInvestorData] = useState([]);
  const [investorLoading, setInvestorLoading] = useState(false);
  const [investorError, setInvestorError] = useState(null);

  // Audit Trail state
  const [auditFilters, setAuditFilters] = useState({
    action: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  // Current vs Long-Term state
  const [portionFilters, setPortionFilters] = useState({
    asOfDate: '',
    search: '',
    productId: '',
    currencyId: '',
    investor: '',
  });
  const [portionData, setPortionData] = useState([]);
  const [portionLoading, setPortionLoading] = useState(false);
  const [portionError, setPortionError] = useState(null);

  // Rolled Contracts state
  const [rolledFilters, setRolledFilters] = useState({
    dateFrom: '',
    dateTo: '',
  });
  const [rolledData, setRolledData] = useState([]);
  const [rolledLoading, setRolledLoading] = useState(false);
  const [rolledError, setRolledError] = useState(null);

  // Rollover Fund Flow state
  const [fundFlowFilters, setFundFlowFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: '',
    unallocatedOnly: false,
  });
  const [fundFlowData, setFundFlowData] = useState([]);
  const [fundFlowLoading, setFundFlowLoading] = useState(false);
  const [fundFlowError, setFundFlowError] = useState(null);

  // Contract Suspense state
  const [suspenseFilters, setSuspenseFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: '',
    unallocatedOnly: false,
  });
  const [suspenseData, setSuspenseData] = useState([]);
  const [suspenseLoading, setSuspenseLoading] = useState(false);
  const [suspenseError, setSuspenseError] = useState(null);

  // Interest Accrual Journal state
  const [journalFilters, setJournalFilters] = useState({
    dateFrom: '',
    dateTo: '',
    site: null,
    overrideSiteForUnlinked: '',
    sil: '',
    department: '',
    reExport: false,
  });
  const [journalData, setJournalData] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState(null);
  const [journalExporting, setJournalExporting] = useState(false);
  const [journalSiteOptions, setJournalSiteOptions] = useState([]);

  // Dropdown data for portion filters (product + currency only)
  const [portionDropdowns, setPortionDropdowns] = useState({
    products: [],
    currencies: [],
    loaded: false,
  });

  const loadPortionDropdowns = async () => {
    if (portionDropdowns.loaded) return;
    try {
      const [productsRes, currenciesRes] = await Promise.all([
        apiGet('/refdata/investment-types?active_only=true'),
        apiGet('/refdata/countries?active_only=true'),
      ]);
      setPortionDropdowns({
        products: (productsRes.data || productsRes || []),
        currencies: (currenciesRes.data || currenciesRes || []),
        loaded: true,
      });
    } catch (err) {
      console.error('Error loading dropdowns:', err);
    }
  };

  // Load Contract Register data
  const loadContractData = async () => {
    setContractLoading(true);
    setContractError(null);
    try {
      let url = '/reports/contract-register?limit=500';
      if (contractFilters.status !== 'all') {
        url += `&status=${contractFilters.status}`;
      }
      if (contractFilters.dateFrom) {
        url += `&date_from=${contractFilters.dateFrom}`;
      }
      if (contractFilters.dateTo) {
        url += `&date_to=${contractFilters.dateTo}`;
      }

      const response = await apiGet(url);
      setContractData(extractApiData(response));
    } catch (err) {
      console.error('Error loading contracts:', err);
      setContractError('Failed to load contract data. Please try again.');
    } finally {
      setContractLoading(false);
    }
  };

  // Load Payment Schedule data
  const loadPaymentData = async () => {
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      let url = '/reports/payment-schedule?limit=500';
      if (paymentFilters.dateFrom) {
        url += `&date_from=${paymentFilters.dateFrom}`;
      }
      if (paymentFilters.dateTo) {
        url += `&date_to=${paymentFilters.dateTo}`;
      }

      const response = await apiGet(url);
      setPaymentData(extractApiData(response));
    } catch (err) {
      console.error('Error loading payments:', err);
      setPaymentError('Failed to load payment data. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Load Investor Summary data
  const loadInvestorData = async () => {
    setInvestorLoading(true);
    setInvestorError(null);
    try {
      const response = await apiGet('/stakeholders/investors?limit=100');
      setInvestorData(extractApiData(response));
    } catch (err) {
      console.error('Error loading investors:', err);
      setInvestorError('Failed to load investor data. Please try again.');
    } finally {
      setInvestorLoading(false);
    }
  };

  // Load Audit Trail data
  const loadAuditData = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      let url = '/admin/audit?limit=100';
      if (auditFilters.action !== 'all') {
        url += `&action=${auditFilters.action}`;
      }
      if (auditFilters.dateFrom) {
        url += `&date_from=${auditFilters.dateFrom}`;
      }
      if (auditFilters.dateTo) {
        url += `&date_to=${auditFilters.dateTo}`;
      }

      const response = await apiGet(url);
      setAuditData(extractApiData(response));
    } catch (err) {
      console.error('Error loading audit trail:', err);
      setAuditError('Failed to load audit data. Please try again.');
    } finally {
      setAuditLoading(false);
    }
  };

  // Export handlers
  const handleExportContracts = () => {
    if (contractData.length > 0) {
      exportContractRegister(contractData);
    }
  };

  const handleExportPayments = () => {
    if (paymentData.length > 0) {
      exportPaymentSchedule(paymentData);
    }
  };

  const handleExportInvestors = () => {
    if (investorData.length > 0) {
      exportInvestorSummary(investorData);
    }
  };

  const handleExportAudit = () => {
    if (auditData.length > 0) {
      exportAuditTrail(auditData);
    }
  };

  // Load Current vs Long-Term data
  const loadPortionData = async () => {
    setPortionLoading(true);
    setPortionError(null);
    setExpandedRows({});
    setDetailData({});
    setDetailLoading({});
    try {
      let url = '/reports/current-long-term?limit=1000';
      if (portionFilters.asOfDate) url += `&as_of_date=${portionFilters.asOfDate}`;
      if (portionFilters.search) url += `&search=${encodeURIComponent(portionFilters.search)}`;
      if (portionFilters.productId) url += `&product_id=${portionFilters.productId}`;
      if (portionFilters.currencyId) url += `&currency_id=${portionFilters.currencyId}`;
      if (portionFilters.investor) url += `&investor=${encodeURIComponent(portionFilters.investor)}`;

      const response = await apiGet(url);
      setPortionData(extractApiData(response));
    } catch (err) {
      console.error('Error loading current/long-term portions:', err);
      setPortionError('Failed to load portion data. Please try again.');
    } finally {
      setPortionLoading(false);
    }
  };

  const handleExportPortions = () => {
    if (portionData.length > 0) {
      exportCurrentLongTerm(portionData);
    }
  };

  // Load Rolled Contracts data
  const loadRolledData = async () => {
    setRolledLoading(true);
    setRolledError(null);
    try {
      let url = '/reports/rolled-contracts?limit=1000';
      if (rolledFilters.dateFrom) url += `&date_from=${rolledFilters.dateFrom}`;
      if (rolledFilters.dateTo) url += `&date_to=${rolledFilters.dateTo}`;

      const response = await apiGet(url);
      setRolledData(extractApiData(response));
    } catch (err) {
      console.error('Error loading rolled contracts:', err);
      setRolledError('Failed to load rolled contract data. Please try again.');
    } finally {
      setRolledLoading(false);
    }
  };

  const handleExportRolled = () => {
    if (rolledData.length > 0) {
      exportRolledContracts(rolledData);
    }
  };

  // Load Rollover Fund Flow data
  const loadFundFlowData = async () => {
    setFundFlowLoading(true);
    setFundFlowError(null);
    try {
      let url = '/reports/rollover-fund-flow?limit=1000';
      if (fundFlowFilters.dateFrom) url += `&date_from=${fundFlowFilters.dateFrom}`;
      if (fundFlowFilters.dateTo) url += `&date_to=${fundFlowFilters.dateTo}`;
      if (fundFlowFilters.search) url += `&search=${encodeURIComponent(fundFlowFilters.search)}`;
      if (fundFlowFilters.unallocatedOnly) url += '&unallocated_only=true';

      const response = await apiGet(url);
      setFundFlowData(extractApiData(response));
    } catch (err) {
      console.error('Error loading rollover fund flow:', err);
      setFundFlowError('Failed to load rollover fund flow data. Please try again.');
    } finally {
      setFundFlowLoading(false);
    }
  };

  const handleExportFundFlow = () => {
    if (fundFlowData.length > 0) {
      exportRolloverFundFlow(fundFlowData);
    }
  };

  // Load Contract Suspense data
  const loadSuspenseData = async () => {
    setSuspenseLoading(true);
    setSuspenseError(null);
    try {
      let url = '/reports/contract-suspense?limit=1000';
      if (suspenseFilters.dateFrom) url += `&date_from=${suspenseFilters.dateFrom}`;
      if (suspenseFilters.dateTo) url += `&date_to=${suspenseFilters.dateTo}`;
      if (suspenseFilters.search) url += `&search=${encodeURIComponent(suspenseFilters.search)}`;
      if (suspenseFilters.unallocatedOnly) url += '&unallocated_only=true';

      const response = await apiGet(url);
      setSuspenseData(extractApiData(response));
    } catch (err) {
      console.error('Error loading contract suspense:', err);
      setSuspenseError('Failed to load contract suspense data. Please try again.');
    } finally {
      setSuspenseLoading(false);
    }
  };

  const handleExportSuspense = () => {
    if (suspenseData.length > 0) {
      exportContractSuspense(suspenseData);
    }
  };

  // Load Interest Accrual Journal data (preview)
  const loadJournalData = async () => {
    setJournalLoading(true);
    setJournalError(null);
    try {
      let url = '/reports/interest-accrual-journal?';
      if (journalFilters.dateFrom) url += `date_from=${journalFilters.dateFrom}&`;
      if (journalFilters.dateTo) url += `date_to=${journalFilters.dateTo}&`;
      if (journalFilters.site) url += `site=${encodeURIComponent(journalFilters.site)}&`;
      url += 'include_exported=true';

      const response = await apiGet(url);
      setJournalData(extractApiData(response));
    } catch (err) {
      console.error('Error loading journal data:', err);
      setJournalError('Failed to load interest accrual journal data.');
    } finally {
      setJournalLoading(false);
    }
  };

  // Export Interest Accrual Journal to xlsx (marks entries as exported)
  const handleExportJournal = async () => {
    setJournalExporting(true);
    setJournalError(null);
    try {
      const blob = await apiDownload('/reports/interest-accrual-journal/export', {
        date_from: journalFilters.dateFrom,
        date_to: journalFilters.dateTo,
        site: journalFilters.site,
        override_site_for_unlinked:
          journalFilters.site === '__none__' ? journalFilters.overrideSiteForUnlinked : '',
        sil: journalFilters.sil,
        department: journalFilters.department,
        re_export: journalFilters.reExport,
      });
      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Interest_Accrual_Journal_${journalFilters.dateFrom}_${journalFilters.dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      // Reload preview to show updated exported flags
      loadJournalData();
    } catch (err) {
      console.error('Error exporting journal:', err);
      const msg = err?.response?.status === 404
        ? 'No unexported accrual entries found for this date range.'
        : 'Failed to export journal. Please try again.';
      setJournalError(msg);
    } finally {
      setJournalExporting(false);
    }
  };

  // Drill-down state for Current vs Long-Term
  const [expandedRows, setExpandedRows] = useState({});
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState({});

  const toggleRowExpand = useCallback(async (contractId, investorId) => {
    const key = `${contractId}-${investorId || 'all'}`;
    if (expandedRows[key]) {
      setExpandedRows(prev => ({ ...prev, [key]: false }));
      return;
    }

    setExpandedRows(prev => ({ ...prev, [key]: true }));

    // Only fetch if we don't already have data
    if (!detailData[key]) {
      setDetailLoading(prev => ({ ...prev, [key]: true }));
      try {
        let url = `/reports/current-long-term/${contractId}/detail`;
        const params = [];
        if (investorId) params.push(`investor_id=${investorId}`);
        if (portionFilters.asOfDate) params.push(`as_of_date=${portionFilters.asOfDate}`);
        if (params.length) url += '?' + params.join('&');

        const response = await apiGet(url);
        setDetailData(prev => ({ ...prev, [key]: extractApiData(response) }));
      } catch (err) {
        console.error('Error loading detail:', err);
        setDetailData(prev => ({ ...prev, [key]: [] }));
      } finally {
        setDetailLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  }, [expandedRows, detailData, portionFilters.asOfDate]);

  // Sub-feature tab filtering
  const allReportTabs = useMemo(() => [
    { feature: 'contract_register', label: 'Contract Register' },
    { feature: 'payment_schedule', label: 'Payment Schedule' },
    { feature: 'investor_summary', label: 'Investor Summary' },
    { feature: 'current_long_term', label: 'Current vs Long-Term' },
    { feature: 'rolled_contracts', label: 'Rolled Contracts' },
    { feature: 'rollover_fund_flow', label: 'Rollover Fund Flow' },
    { feature: 'contract_suspense', label: 'Contract Suspense' },
    { feature: 'interest_accrual_journal', label: 'Interest Accrual Journal' },
    { feature: 'audit_trail', label: 'Audit Trail' },
  ], []);

  const visibleReportTabs = useMemo(() =>
    allReportTabs.filter(t =>
      isFeatureEnabled('reports', t.feature) &&
      (t.feature !== 'audit_trail' || isAdmin)
    ),
    [allReportTabs, isFeatureEnabled, isAdmin]
  );

  const safeTabValue = visibleReportTabs.length > 0 ? Math.min(tabValue, visibleReportTabs.length - 1) : 0;
  const activeReportFeature = visibleReportTabs[safeTabValue]?.feature;

  // Load site dropdown options when the journal section becomes visible
  useEffect(() => {
    if (activeReportFeature !== 'interest_accrual_journal') return;
    if (journalSiteOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/reports/interest-accrual-journal/sites');
        const rows = extractApiData(res);
        const sites = Array.isArray(rows) ? rows.map(r => r.site).filter(Boolean) : [];
        if (!cancelled) setJournalSiteOptions(sites);
      } catch (err) {
        console.error('Error loading journal site options:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeReportFeature, journalSiteOptions.length]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={safeTabValue} onChange={(e, v) => setTabValue(v)}>
          {visibleReportTabs.map(t => (
            <Tab key={t.feature} label={t.label} />
          ))}
        </Tabs>
      </Paper>
      {/* Contract Register */}
      {activeReportFeature === 'contract_register' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contract Register
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={contractFilters.status}
                    onChange={(e) => setContractFilters({ ...contractFilters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Contracts</MenuItem>
                    <MenuItem value="Active">Active Only</MenuItem>
                    <MenuItem value="Matured">Matured</MenuItem>
                    <MenuItem value="Draft">Drafts</MenuItem>
                    <MenuItem value="Pending Approval">Pending Approval</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={contractFilters.dateFrom}
                  onChange={(e) => setContractFilters({ ...contractFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={contractFilters.dateTo}
                  onChange={(e) => setContractFilters({ ...contractFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  variant="contained"
                  startIcon={contractLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadContractData}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={contractLoading}
                >
                  {contractLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportContracts}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={contractData.length === 0}
                >
                  Export Excel ({contractData.length})
                </Button>
              </Grid>
            </Grid>

            {contractError && (
              <Alert severity="error" sx={{ mb: 2 }}>{contractError}</Alert>
            )}

            {contractData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Capital Amount</TableCell>
                      <TableCell align="right">Rate %</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Maturity Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contractData.map((contract) => (
                      <TableRow key={contract.id} hover>
                        <TableCell>{contract.reference || contract.contract_reference}</TableCell>
                        <TableCell>{contract.investor_name}</TableCell>
                        <TableCell>{contract.product_type || contract.investment_type}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(contract.capital_amount, contract.currency_code)}
                        </TableCell>
                        <TableCell align="right">{contract.interest_rate}%</TableCell>
                        <TableCell>{formatDate(contract.deposit_date)}</TableCell>
                        <TableCell>{formatDate(contract.maturity_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={contract.status}
                            size="small"
                            color={getStatusColor(contract.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load contract data
                </Typography>
              </Paper>
            )}

            {contractData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {contractData.length} contract(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Payment Schedule */}
      {activeReportFeature === 'payment_schedule' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Schedule Report
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={paymentFilters.dateFrom}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={paymentFilters.dateTo}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="contained"
                  startIcon={paymentLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadPaymentData}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPayments}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={paymentData.length === 0}
                >
                  Export Excel ({paymentData.length})
                </Button>
              </Grid>
            </Grid>

            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>
            )}

            {paymentData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contract Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="right">Total Payment</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentData.map((payment, index) => (
                      <TableRow key={payment.id || index} hover>
                        <TableCell>{payment.contract_reference}</TableCell>
                        <TableCell>{payment.investor_name || '-'}</TableCell>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell>{payment.period_number}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.interest_amount, payment.currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.capital_amount, payment.currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.payment_amount, payment.currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.closing_balance, payment.currency_code)}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status || 'pending'}
                            size="small"
                            color={
                              payment.status === 'paid' ? 'success' :
                              payment.status === 'overdue' ? 'error' :
                              payment.status === 'due_soon' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load payment schedule
                </Typography>
              </Paper>
            )}

            {paymentData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {paymentData.length} payment(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Investor Summary */}
      {activeReportFeature === 'investor_summary' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Investor Summary Report
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={investorFilters.currency}
                    onChange={(e) => setInvestorFilters({ ...investorFilters, currency: e.target.value })}
                    label="Currency"
                  >
                    <MenuItem value="all">All Currencies</MenuItem>
                    <MenuItem value="ZAR">ZAR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="BWP">BWP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Button
                  variant="contained"
                  startIcon={investorLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadInvestorData}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={investorLoading}
                >
                  {investorLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportInvestors}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={investorData.length === 0}
                >
                  Export Excel ({investorData.length})
                </Button>
              </Grid>
            </Grid>

            {investorError && (
              <Alert severity="error" sx={{ mb: 2 }}>{investorError}</Alert>
            )}

            {investorData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Investor Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Total Contracts</TableCell>
                      <TableCell align="right">Active Contracts</TableCell>
                      <TableCell align="right">Total Capital</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investorData.map((investor) => (
                      <TableRow key={investor.id} hover>
                        <TableCell>{investor.name}</TableCell>
                        <TableCell>{investor.type}</TableCell>
                        <TableCell>{investor.domicile_country_name}</TableCell>
                        <TableCell>{investor.currency_code}</TableCell>
                        <TableCell align="right">{investor.total_contracts || 0}</TableCell>
                        <TableCell align="right">{investor.active_contracts || 0}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(investor.total_capital, investor.currency_code)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={investor.active ? 'Active' : 'Inactive'}
                            size="small"
                            color={investor.active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load investor summary
                </Typography>
              </Paper>
            )}

            {investorData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {investorData.length} investor(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Current vs Long-Term Portions */}
      {activeReportFeature === 'current_long_term' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current vs Long-Term Portions
            </Typography>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="As-Of Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={portionFilters.asOfDate}
                  onChange={(e) => setPortionFilters({ ...portionFilters, asOfDate: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Reference..."
                  value={portionFilters.search}
                  onChange={(e) => setPortionFilters({ ...portionFilters, search: e.target.value })}
                  size="small"
                  onKeyDown={(e) => e.key === 'Enter' && loadPortionData()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="Investor"
                  placeholder="Name..."
                  value={portionFilters.investor}
                  onChange={(e) => setPortionFilters({ ...portionFilters, investor: e.target.value })}
                  size="small"
                  onKeyDown={(e) => e.key === 'Enter' && loadPortionData()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={portionFilters.productId}
                    onChange={(e) => setPortionFilters({ ...portionFilters, productId: e.target.value })}
                    label="Product"
                    onOpen={loadPortionDropdowns}
                  >
                    <MenuItem value="">All</MenuItem>
                    {portionDropdowns.products.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 1.5
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={portionFilters.currencyId}
                    onChange={(e) => setPortionFilters({ ...portionFilters, currencyId: e.target.value })}
                    label="Currency"
                    onOpen={loadPortionDropdowns}
                  >
                    <MenuItem value="">All</MenuItem>
                    {[...new Map(portionDropdowns.currencies.map(c => [c.currency_code, c])).values()]
                      .filter(c => c.currency_code)
                      .map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.currency_code}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 1.25
                }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={portionLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  onClick={loadPortionData}
                  fullWidth
                  disabled={portionLoading}
                >
                  {portionLoading ? 'Loading...' : 'Generate'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 1.25
                }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPortions}
                  fullWidth
                  disabled={portionData.length === 0}
                >
                  Export ({portionData.length})
                </Button>
              </Grid>
            </Grid>

            {portionError && (
              <Alert severity="error" sx={{ mb: 2 }}>{portionError}</Alert>
            )}

            {portionData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell>Reference</TableCell>
                      <TableCell>Ext. Reference</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>CCY</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="right">Rate %</TableCell>
                      <TableCell>Deposit</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Paid Total</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>Current Total</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>LT Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Outstanding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {portionData.map((row, index) => {
                      const rowKey = `${row.contract_id}-${row.investor_id || 'all'}`;
                      const isExpanded = expandedRows[rowKey];
                      const entries = detailData[rowKey] || [];
                      const isDetailLoading = detailLoading[rowKey];
                      return (
                        <React.Fragment key={`${row.contract_id}-${index}`}>
                          <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRowExpand(row.contract_id, row.investor_id)}>
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              <IconButton size="small">
                                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>{row.contract_reference}</TableCell>
                            <TableCell>{row.user_reference || '-'}</TableCell>
                            <TableCell>{row.investor_name}</TableCell>
                            <TableCell>{row.product_type || '-'}</TableCell>
                            <TableCell>{row.currency_code}</TableCell>
                            <TableCell align="right">{formatNum(row.capital_amount, 2)}</TableCell>
                            <TableCell align="right">{row.interest_rate}%</TableCell>
                            <TableCell>{formatDate(row.deposit_date)}</TableCell>
                            <TableCell>{formatDate(row.maturity_date)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>{formatNum(row.paid_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff', fontWeight: 'bold' }}>{formatNum(row.current_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0', fontWeight: 'bold' }}>{formatNum(row.long_term_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNum(row.total_outstanding, 2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={23}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ m: 1, ml: 6 }}>
                                  {isDetailLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                                      <CircularProgress size={16} />
                                      <Typography variant="body2">Loading schedule entries...</Typography>
                                    </Box>
                                  ) : entries.length > 0 ? (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Payment Date</TableCell>
                                          <TableCell>Narration</TableCell>
                                          <TableCell>Bucket</TableCell>
                                          <TableCell align="right">Capital</TableCell>
                                          <TableCell align="right">Interest</TableCell>
                                          <TableCell align="right">WHT</TableCell>
                                          <TableCell align="right">Payment</TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {entries.map((entry) => (
                                          <TableRow key={entry.schedule_id}>
                                            <TableCell>{formatDate(entry.payment_date)}</TableCell>
                                            <TableCell>{entry.narration}</TableCell>
                                            <TableCell>
                                              <Chip
                                                label={entry.bucket}
                                                size="small"
                                                color={entry.bucket === 'Paid' ? 'default' : entry.bucket === 'Current' ? 'info' : 'warning'}
                                              />
                                            </TableCell>
                                            <TableCell align="right">{formatNum(entry.capital_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.interest_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.wht_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.payment_amount, 2)}</TableCell>
                                            <TableCell>
                                              {entry.is_paid && <Chip label="Matched" size="small" color="success" />}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                                      No schedule entries found
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load current vs long-term portions
                </Typography>
              </Paper>
            )}

            {portionData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {portionData.length} contract(s) - Current portion: next 12 months from {portionFilters.asOfDate ? formatDate(portionFilters.asOfDate) : 'start of current month'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Rolled Contracts */}
      {activeReportFeature === 'rolled_contracts' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Rolled Contracts</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Audit report showing contract rollover chains — which contracts were rolled into new contracts, with amounts and dates.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rolledFilters.dateFrom}
                  onChange={(e) => setRolledFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rolledFilters.dateTo}
                  onChange={(e) => setRolledFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="contained"
                  startIcon={rolledLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                  onClick={loadRolledData}
                  disabled={rolledLoading}
                >
                  Generate Preview
                </Button>
              </Grid>
              <Grid
                sx={{ textAlign: 'right' }}
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportRolled}
                  disabled={rolledData.length === 0}
                >
                  Export
                </Button>
              </Grid>
            </Grid>

            {rolledError && <Alert severity="error" sx={{ mb: 2 }}>{rolledError}</Alert>}

            {rolledData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Original Ref</TableCell>
                      <TableCell>Original Ext Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell align="right">Original Capital</TableCell>
                      <TableCell>CCY</TableCell>
                      <TableCell>Original Maturity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>New Ref</TableCell>
                      <TableCell>New Ext Ref</TableCell>
                      <TableCell align="right">Rolled Amount</TableCell>
                      <TableCell align="right">New Funds</TableCell>
                      <TableCell align="right">New Capital</TableCell>
                      <TableCell>New Deposit</TableCell>
                      <TableCell>New Maturity</TableCell>
                      <TableCell>New Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rolledData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.original_contract_ref}</TableCell>
                        <TableCell>{row.original_user_ref || '-'}</TableCell>
                        <TableCell>{row.original_investor_name}</TableCell>
                        <TableCell align="right">{formatCurrency(row.original_capital_amount, row.original_currency_code)}</TableCell>
                        <TableCell>{row.original_currency_code}</TableCell>
                        <TableCell>{formatDate(row.original_maturity_date)}</TableCell>
                        <TableCell>
                          <Chip label={row.original_status} size="small" color={getStatusColor(row.original_status)} />
                        </TableCell>
                        <TableCell>{row.new_contract_ref}</TableCell>
                        <TableCell>{row.new_user_ref || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(row.rolled_amount, row.original_currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.new_funds_amount, row.original_currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.new_capital_amount, row.original_currency_code)}</TableCell>
                        <TableCell>{formatDate(row.new_deposit_date)}</TableCell>
                        <TableCell>{formatDate(row.new_maturity_date)}</TableCell>
                        <TableCell>
                          <Chip label={row.new_status} size="small" color={getStatusColor(row.new_status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load rolled contracts data
                </Typography>
              </Paper>
            )}

            {rolledData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {rolledData.length} rolled contract record(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Rollover Fund Flow */}
      {activeReportFeature === 'rollover_fund_flow' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Rollover Fund Flow</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Per-investor breakdown of how capital was allocated during rollovers — rolled into new contracts, paid out, or remaining unallocated.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fundFlowFilters.dateFrom}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fundFlowFilters.dateTo}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  label="Search"
                  size="small"
                  fullWidth
                  placeholder="Contract ref / investor"
                  value={fundFlowFilters.search}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fundFlowFilters.unallocatedOnly}
                      onChange={(e) => setFundFlowFilters(prev => ({ ...prev, unallocatedOnly: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Unallocated only"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  variant="contained"
                  startIcon={fundFlowLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                  onClick={loadFundFlowData}
                  disabled={fundFlowLoading}
                >
                  Generate
                </Button>
              </Grid>
              <Grid
                sx={{ textAlign: 'right' }}
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportFundFlow}
                  disabled={fundFlowData.length === 0}
                >
                  Export
                </Button>
              </Grid>
            </Grid>

            {fundFlowError && <Alert severity="error" sx={{ mb: 2 }}>{fundFlowError}</Alert>}

            {fundFlowData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Contract</TableCell>
                      <TableCell>Ext Ref</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="center">WHT %</TableCell>
                      <TableCell align="center">Gross-Up</TableCell>
                      <TableCell align="right">Rolled</TableCell>
                      <TableCell align="right">Paid Out</TableCell>
                      <TableCell align="right">Unallocated</TableCell>
                      <TableCell>Destinations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fundFlowData.map((row, idx) => {
                      const destinations = (row.rolled_to_contracts && row.rolled_to_contracts !== '[]')
                        ? (typeof row.rolled_to_contracts === 'string' ? JSON.parse(row.rolled_to_contracts) : row.rolled_to_contracts)
                        : [];
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{row.source_contract_ref}</TableCell>
                          <TableCell>{row.source_user_ref || '-'}</TableCell>
                          <TableCell>
                            <Chip label={row.source_status} size="small" color={getStatusColor(row.source_status)} />
                          </TableCell>
                          <TableCell>{formatDate(row.source_maturity_date)}</TableCell>
                          <TableCell>{row.investor_name}</TableCell>
                          <TableCell align="right">{formatCurrency(row.investor_capital, row.currency_code)}</TableCell>
                          <TableCell align="center">{row.wht_rate}%</TableCell>
                          <TableCell align="center">
                            <Chip label={row.wht_gross_up ? 'Yes' : 'No'} size="small"
                              color={row.wht_gross_up ? 'success' : 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(row.total_rolled, row.currency_code)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.total_paid_out, row.currency_code)}</TableCell>
                          <TableCell align="right" sx={{
                            color: parseFloat(row.remaining_unallocated) > 0 ? 'warning.main' : undefined,
                            fontWeight: parseFloat(row.remaining_unallocated) > 0 ? 'bold' : undefined,
                          }}>
                            {formatCurrency(row.remaining_unallocated, row.currency_code)}
                          </TableCell>
                          <TableCell>
                            {destinations.map((d, i) => (
                              <Chip key={i} label={`${d.reference} (${formatCurrency(d.rolled_amount)})`}
                                size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate" to load rollover fund flow data
                </Typography>
              </Paper>
            )}

            {fundFlowData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {fundFlowData.length} record(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Contract Suspense */}
      {activeReportFeature === 'contract_suspense' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Contract Suspense</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Per-investor breakdown of funds held in suspense after contract finalisation — showing how much has been allocated to new contracts and what remains.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={suspenseFilters.dateFrom}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={suspenseFilters.dateTo}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  label="Search"
                  size="small"
                  fullWidth
                  placeholder="Contract ref / investor"
                  value={suspenseFilters.search}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={suspenseFilters.unallocatedOnly}
                      onChange={(e) => setSuspenseFilters(prev => ({ ...prev, unallocatedOnly: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Unallocated only"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  variant="contained"
                  startIcon={suspenseLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                  onClick={loadSuspenseData}
                  disabled={suspenseLoading}
                >
                  Generate
                </Button>
              </Grid>
              <Grid sx={{ textAlign: 'right' }} size={{ xs: 12, md: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportSuspense}
                  disabled={suspenseData.length === 0}
                >
                  Export
                </Button>
              </Grid>
            </Grid>

            {suspenseError && <Alert severity="error" sx={{ mb: 2 }}>{suspenseError}</Alert>}

            {suspenseData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Contract</TableCell>
                      <TableCell>Ext Ref</TableCell>
                      <TableCell>Finalisation Date</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell align="right">Net Value</TableCell>
                      <TableCell align="right">Suspense</TableCell>
                      <TableCell align="right">Allocated</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="right">Paid Out</TableCell>
                      <TableCell>Destinations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suspenseData.map((row, idx) => {
                      const destinations = (row.rolled_to_contracts && row.rolled_to_contracts !== '[]')
                        ? (typeof row.rolled_to_contracts === 'string' ? JSON.parse(row.rolled_to_contracts) : row.rolled_to_contracts)
                        : [];
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{row.source_contract_ref}</TableCell>
                          <TableCell>{row.source_user_ref || '-'}</TableCell>
                          <TableCell>{formatDate(row.finalisation_date)}</TableCell>
                          <TableCell>{row.investor_name}</TableCell>
                          <TableCell align="right">{formatCurrency(row.net_value, row.currency_code)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.suspense_amount, row.currency_code)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.allocated_to_new_contracts, row.currency_code)}</TableCell>
                          <TableCell align="right" sx={{
                            color: parseFloat(row.remaining_unallocated) > 0 ? 'warning.main' : undefined,
                            fontWeight: parseFloat(row.remaining_unallocated) > 0 ? 'bold' : undefined,
                          }}>
                            {formatCurrency(row.remaining_unallocated, row.currency_code)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(row.payout_amount, row.currency_code)}</TableCell>
                          <TableCell>
                            {destinations.map((d, i) => (
                              <Chip key={i} label={`${d.reference} (${formatCurrency(d.rolled_amount)})`}
                                size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate" to load contract suspense data
                </Typography>
              </Paper>
            )}

            {suspenseData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {suspenseData.length} record(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Interest Accrual Journal */}
      {activeReportFeature === 'interest_accrual_journal' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Interest Accrual Journal Export
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={journalFilters.dateFrom}
                  onChange={(e) => setJournalFilters({ ...journalFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={journalFilters.dateTo}
                  onChange={(e) => setJournalFilters({ ...journalFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 1.5
                }}>
                <Autocomplete
                  options={['', '__none__', ...journalSiteOptions]}
                  value={journalFilters.site || ''}
                  onChange={(_, newValue) => setJournalFilters({
                    ...journalFilters,
                    site: newValue || null,
                    overrideSiteForUnlinked: newValue === '__none__' ? journalFilters.overrideSiteForUnlinked : '',
                  })}
                  getOptionLabel={(option) => {
                    if (option === '' || option == null) return 'All Sites';
                    if (option === '__none__') return '(No Site)';
                    return option;
                  }}
                  isOptionEqualToValue={(option, val) => (option || '') === (val || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Site" placeholder="Search..." />
                  )}
                />
              </Grid>
              {/* SIL + Department (and the inline override when "(No Site)" is active)
                  share one cell that flex-splits, so all three shrink together
                  without expanding the overall row width. */}
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {journalFilters.site === '__none__' && (
                    <TextField
                      sx={{ flex: 1, minWidth: 0 }}
                      label="Export site *"
                      inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                      value={journalFilters.overrideSiteForUnlinked}
                      onChange={(e) => setJournalFilters({
                        ...journalFilters,
                        overrideSiteForUnlinked: (e.target.value || '').toUpperCase(),
                      })}
                      error={
                        !!journalFilters.overrideSiteForUnlinked &&
                        journalFilters.overrideSiteForUnlinked.length !== 5
                      }
                    />
                  )}
                  <TextField
                    sx={{ flex: 1, minWidth: 0 }}
                    label="SIL"
                    value={journalFilters.sil}
                    onChange={(e) => setJournalFilters({ ...journalFilters, sil: e.target.value })}
                  />
                  <TextField
                    sx={{ flex: 1, minWidth: 0 }}
                    label="Department"
                    value={journalFilters.department}
                    onChange={(e) => setJournalFilters({ ...journalFilters, department: e.target.value })}
                  />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 1.5
                }}>
                <Button
                  variant="contained"
                  startIcon={journalLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadJournalData}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={journalLoading || !journalFilters.dateFrom || !journalFilters.dateTo}
                >
                  {journalLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={journalExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportJournal}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={
                    journalExporting ||
                    journalData.length === 0 ||
                    !journalFilters.dateFrom ||
                    !journalFilters.dateTo ||
                    (!journalFilters.reExport && journalData.filter(r => !r.journal_exported_at).length === 0) ||
                    (journalFilters.site === '__none__' && journalFilters.overrideSiteForUnlinked.length !== 5)
                  }
                >
                  {journalExporting ? 'Exporting...' : journalFilters.reExport
                    ? `Re-export All (${journalData.length})`
                    : `Extract to Excel (${journalData.filter(r => !r.journal_exported_at).length})`
                  }
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={journalFilters.reExport}
                    onChange={(e) => setJournalFilters({ ...journalFilters, reExport: e.target.checked })}
                    size="small"
                  />
                }
                label="Include previously exported entries in extract"
              />
            </Box>

            {journalError && (
              <Alert severity="error" sx={{ mb: 2 }}>{journalError}</Alert>
            )}

            {journalData.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {journalData.filter(r => !r.journal_exported_at).length} new entry(ies) ready for export.
                {journalData.filter(r => r.journal_exported_at).length > 0 &&
                  ` ${journalData.filter(r => r.journal_exported_at).length} previously exported.`}
              </Alert>
            )}

            {journalData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contract Ref</TableCell>
                      <TableCell>External Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell>Expense GL</TableCell>
                      <TableCell>Control GL</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journalData.map((row, index) => {
                      const isReversal = row.narration === 'Interest Reversal';
                      const isReaccrual = row.narration === 'Interest Re-accrual';
                      const rowSx = {
                        ...(row.journal_exported_at ? { opacity: 0.6 } : {}),
                        ...(isReversal ? { backgroundColor: '#fff4e5' } : {}),
                        ...(isReaccrual ? { backgroundColor: '#e8f4fd' } : {}),
                      };
                      const typeChip = isReversal
                        ? <Chip label="Reversal" size="small" color="warning" />
                        : isReaccrual
                          ? <Chip label="Re-accrual" size="small" color="info" />
                          : <Chip label="Accrual" size="small" variant="outlined" />;
                      return (
                        <TableRow
                          key={row.schedule_id || index}
                          hover
                          sx={rowSx}
                          title={
                            (isReversal || isReaccrual) && row.reversal_source_schedule_id
                              ? `Paired with schedule entry #${row.reversal_source_schedule_id}`
                              : undefined
                          }
                        >
                          <TableCell>{row.contract_reference}</TableCell>
                          <TableCell>{row.user_reference || '-'}</TableCell>
                          <TableCell>{row.investor_name || '-'}</TableCell>
                          <TableCell>{row.product_reference || row.product_type}</TableCell>
                          <TableCell>{row.currency_code}</TableCell>
                          <TableCell>{formatDate(row.payment_date)}</TableCell>
                          <TableCell>{typeChip}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.interest_amount, row.currency_code)}
                          </TableCell>
                          <TableCell>{row.dr_expense_account || '-'}</TableCell>
                          <TableCell>{row.cr_control_account || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.journal_exported_at ? 'Exported' : 'New'}
                              size="small"
                              color={row.journal_exported_at ? 'default' : 'success'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Set a date range and click "Preview" to load interest accrual entries
                </Typography>
              </Paper>
            )}

            {journalData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {journalData.length} entry(ies)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Audit Trail */}
      {activeReportFeature === 'audit_trail' && (
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Audit Trail Report
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={auditFilters.action}
                    onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })}
                    label="Action Type"
                  >
                    <MenuItem value="all">All Actions</MenuItem>
                    <MenuItem value="INSERT">Create</MenuItem>
                    <MenuItem value="UPDATE">Update</MenuItem>
                    <MenuItem value="DELETE">Delete</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={auditFilters.dateFrom}
                  onChange={(e) => setAuditFilters({ ...auditFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={auditFilters.dateTo}
                  onChange={(e) => setAuditFilters({ ...auditFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  variant="contained"
                  startIcon={auditLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadAuditData}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={auditLoading}
                >
                  {auditLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAudit}
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={auditData.length === 0}
                >
                  Export Excel ({auditData.length})
                </Button>
              </Grid>
            </Grid>

            {auditError && (
              <Alert severity="error" sx={{ mb: 2 }}>{auditError}</Alert>
            )}

            {auditData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Table</TableCell>
                      <TableCell>Record ID</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Amended By</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditData.map((audit, index) => (
                      <TableRow key={audit.id || index} hover>
                        <TableCell>{formatDate(audit.changed_at || audit.created_at)}</TableCell>
                        <TableCell>{audit.changed_by || audit.user_email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={audit.action}
                            size="small"
                            color={
                              audit.action === 'INSERT' ? 'success' :
                              audit.action === 'DELETE' ? 'error' : 'info'
                            }
                          />
                        </TableCell>
                        <TableCell>{audit.table_name}</TableCell>
                        <TableCell>{audit.record_id}</TableCell>
                        <TableCell>{audit.old_values?.created_by || audit.new_values?.created_by || '-'}</TableCell>
                        <TableCell>{audit.new_values?.updated_by || audit.old_values?.updated_by || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {audit.new_values ? JSON.stringify(audit.new_values).substring(0, 80) + '...' : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load audit trail
                </Typography>
              </Paper>
            )}

            {auditData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {auditData.length} audit record(s)
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
    </Box>
  );
}

export default ReportsPage;
